import { SelectProps } from 'antd';
import { useEffect, useState } from 'react';
import { useDebounce } from '@/hooks';
import InfiniteSelect from '@/components/_common/InfiniteSelect';
import useSelectOptions from '@/hooks/useSelectOptions';
import {
  Brand,
  BrandsRequest,
} from '@/core/domain/people/BrandsModelsVersions/BrandsModelsVersions.types';
import {
  useBrandMutation,
  useBrandsFilter,
} from '@/core/domain/people/BrandsModelsVersions/BrandsModelsVersions.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

type Props = Omit<SelectProps, 'options'> & {
  queryFilter?: BrandsRequest;
  queryEnable?: boolean;
  onBrandSelected?: (brand: Brand) => void;
};

const BrandsInfiniteSelect: React.FC<Props> = ({
  queryFilter,
  queryEnable = false,
  ...props
}) => {
  const [selectClicked, setSelectClicked] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>();
  const search = useDebounce(searchValue);

  const brandsList = useBrandsFilter(
    {
      name: search,
      ...queryFilter,
    },
    (queryEnable || selectClicked) && !props.disabled,
  );

  const brands = flatInfiniteData(brandsList.data);
  const brandDetail = useBrandMutation();

  const options = useSelectOptions({
    list: brandsList,
    detail: brandDetail,
    selectProps: props,
    labelFormatter: (brand) => brand.name,
  });

  useEffect(() => {
    if (
      brandDetail.data?.id &&
      brandDetail.data.id === props.value &&
      !!props.onBrandSelected
    ) {
      props.onBrandSelected?.(brandDetail.data);
    }
  }, [brandDetail.data, props.value]);

  return (
    <InfiniteSelect
      fetchNextPage={brandsList.fetchNextPage}
      hasNextPage={brandsList.hasNextPage}
      loading={brandsList.isFetching}
      options={options}
      onSelect={(brandId: number) => {
        setSelectClicked(false);

        const SelectedBrand = brands?.find((brand) => brand.id === brandId);

        if (!!SelectedBrand && !!props.onBrandSelected) {
          props.onBrandSelected(SelectedBrand);
        }
      }}
      onDropdownVisibleChange={(open) => setSelectClicked(open)}
      showSearch
      searchValue={searchValue}
      filterOption={false}
      onSearch={setSearchValue}
      {...props}
    />
  );
};

export default BrandsInfiniteSelect;
